import React from "react";
import _ from "lodash";
import moment from "moment-timezone";
import queryString from "query-string";
import { withRouter } from "react-router-dom";
import LoadingUI from "../common/LoadingUI";
import ModalWrapper from "components/common/ModalWrapper";
import { AnatomyAPI, NetworkAPI } from "apis";
import CstAnatomyPriceSection from "./AnatomyCstPriceSection";
import AnatomyCstFilter from "./AnatomyCstFilter";
import {
  transformReports,
  calculateDashboardReports,
  getSegmentReports,
  getDetectorFloorPriceMap,
} from "./AnatomyCstReportHelper";
import AnatomyDemandTypeFilter from "./AnatomyDemandTypeFilter";
import { PRESETS } from "constants/DemandType";
import AnatomyCstPerformanceSection from "./AnatomyCstPerformanceSection";
import AnatomyTimeAggregationFilter from "./AnatomyTimeAggregationFilter";
import AnatomyCstSegmentsSection from "./AnatomyCstSegmentsSection";
import AnatomyTimezoneTypeSelector from "./AnatomyTimezoneTypeSelector";
import SegmentStickyHeader from "./SegmentStickyHeader";
import UnitDetailHeader from "components/common/UnitDetailHeader";
import GoToAnatomyButton from "components/common/GoToAnatomyButton";
import GoToInsightButton from "components/common/GoToInsightButton";
import GoToAPConfigButton from "components/common/GoToAPConfigButton";
import NetworkStickyTimeWidget from "components/common/NetworkStickyTimeWidget";
import DemandTypeCompareModal from "./DemandTypeCompareModal";
import DetectorMetricModal from "./DetectorMetricModal";
import OptVsDetectorModal from "./OptVsDetectorModal";
import Checkbox from "components/common/Checkbox";
import AnatomyNoPbSelector from "./AnatomyNoPbSelector";
import PublisherUprModal from "./PublisherUprModal";
import PublisherFirstLookButton from "./PublisherFirstLookButton";
import AppliedUprButton from "./AppliedUprButton";
import DemandTypePortfolioButton from "./DemandTypePortfolioButton";
import UnitDemandTypePortfolioButton from "components/common/DemandTypePortfolioButton";
import FixedWidgetContainer from "components/common/FixedWidgetContainer";

import { AnatomyCSTContext } from "./context/AnatomyCSTContext";
import {
  AnatomyFilterContext,
  AnatomyDataContext,
  AnatomyUIContext,
} from "./context/AnatomyContexts";

const DEFAULT = {
  dateRangeNum: 3,
  demandTypePreset: PRESETS.NETWORK_BILLABLE,
  aggregation: 24,
  dataType: "ALL", // "NO_PB"
};

class AnatomyCSTViewer extends React.Component {
  static contextType = AnatomyCSTContext;

  constructor(props) {
    super(props);

    const urlQueryParams = queryString.parse(this.props.location.search);

    const noPbType = _.get(urlQueryParams, "dataType", DEFAULT.dataType);

    this.state = {
      isLoading: false,
      data: null,
      errMsg: null,

      unitInfo: null,
      segmentInfo: null,

      noPbType,

      // date range filter
      dateRangeNum: DEFAULT.dateRangeNum,
      startDate: null,
      endDate: null,
      endDateNotIncludeToday: null,

      // filter
      demandTypePreset: DEFAULT.demandTypePreset,
      demandTypes: null,
      aggregation: DEFAULT.aggregation,
      timezoneType: "NETWORK", // NETWORK, TAIPEI
      isIncludeGhostImp: false,

      filteredData: null,

      // segment
      segmentData: null,
      selectedUprSegmentId: null,
      selectedGroupTypes: null,
      detectorFloorPriceMap: null,

      includeToday: false, // show current day's data
      filterLatestDate: null, // the latest date to filter out

      // dateRange
      st: null,
      et: null,

      // compare chart
      isModalOpen: false,
      detailChartProps: null,

      // detector chart
      isDetectorModalOpen: false,
      detectorChartProps: null,

      // o vs d chart
      isOptVsDetectorModalOpen: false,

      // publisher upr chart
      isPublisherUprModalOpen: false,

      // unit paused
      isOpenWarnModal: true,
    };

    this.getUnitInfo = this.getUnitInfo.bind(this);
    this.getFilterParams = this.getFilterParams.bind(this);
    this.getStartDateEndDate = this.getStartDateEndDate.bind(this);
    this.handleFilterClicked = this.handleFilterClicked.bind(this);
    this.handleGetSegmentReport = this.handleGetSegmentReport.bind(this);
    this.handleDemandTypesChanged = this.handleDemandTypesChanged.bind(this);

    this.recalculateReports = this.recalculateReports.bind(this);
    this.handleTimezoneTypeChanged = this.handleTimezoneTypeChanged.bind(this);
    this.handleNoPbTypeChanged = this.handleNoPbTypeChanged.bind(this);
    this.handleIsIncludeGhostImpChanged =
      this.handleIsIncludeGhostImpChanged.bind(this);

    this.handleSelectedGroupTypes = this.handleSelectedGroupTypes.bind(this);

    this.handleIncludeTodayChanged = this.handleIncludeTodayChanged.bind(this);

    this.handleSetExtremes = this.handleSetExtremes.bind(this);

    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.setDemandTypeCompareProps = this.setDemandTypeCompareProps.bind(this);
    this.setOVDProps = this.setOVDProps.bind(this);
    this.setDetectorProps = this.setDetectorProps.bind(this);
    this.setPublisherUprProps = this.setPublisherUprProps.bind(this);

    this.itemRef = React.createRef();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.unitId !== this.props.unitId) {
      this.setState({ unitId: this.props.unitId });
    }

    if (prevState.unitInfo === null && this.state.unitInfo) {
      // console.log("unitInfo filled");
      // wait for unitInfo to fill in state
      setTimeout(() => {
        this.handleFilterClicked();
      });
    }
  }

  _changeQueryParams(queryParams) {
    this.props.history.push({
      search: "?" + new URLSearchParams(queryParams).toString(),
    });
  }

  getUnitInfo(unitInfo) {
    this.context.setUnitInfo(unitInfo);
    this.setState({
      unitInfo,
      demandTypes: unitInfo.billableDemandTypes,
    });
  }

  getFilterParams() {
    const {
      demandTypePreset,
      demandTypes,
      aggregation,
      includeToday,
      filterLatestDate,
      timezoneType,
      isIncludeGhostImp,
      selectedUprSegmentId,
      unitInfo,
      noPbType,
    } = this.state;

    return {
      demandTypePreset,
      demandTypes,
      aggregation,
      includeToday,
      filterLatestDate,
      timezoneType,
      isIncludeGhostImp,
      segmentId: selectedUprSegmentId,
      costCpm: unitInfo.costCPMInCurrency,
      noPbType,
    };
  }

  setDetectorProps(props) {
    this.setState({
      detectorChartProps: props,
      isDetectorModalOpen: true,
      currentScrollY: window.scrollY,
    });
  }

  setDemandTypeCompareProps() {
    const detailChartProps = {
      groupType: "o",
      layerNum: "all",
      metric: "rrpm",
    };
    this.setState({
      detailChartProps,
      isModalOpen: true,
      currentScrollY: null,
    });
  }

  setOVDProps() {
    const detailChartProps = {
      groupType: "o",
      layerNum: "all",
      metric: "rrpm",
    };
    this.setState({
      detailChartProps,
      isOptVsDetectorModalOpen: true,
      currentScrollY: null,
    });
  }

  setPublisherUprProps() {
    this.setState({
      isPublisherUprModalOpen: true,
      currentScrollY: null,
    });
  }

  handleCloseModal() {
    this.setState({
      isModalOpen: false,
      detailChartProps: null,
      isDetectorModalOpen: false,
      detectorChartProps: null,
      isOptVsDetectorModalOpen: false,
      isPublisherUprModalOpen: false,
    });

    setTimeout(() => {
      if (this.state.currentScrollY) {
        window.scrollTo({
          top: this.state.currentScrollY,
        });
      }
    });
  }

  getStartDateEndDate({
    startDate,
    endDate,
    endDateNotIncludeToday,
    dateRangeNum,
  }) {
    this.setState({ startDate, endDate, endDateNotIncludeToday, dateRangeNum });
  }

  handleDemandTypesChanged(preset, demandTypes) {
    this.setState({ demandTypePreset: preset, demandTypes });
    this.recalculateReports({ demandTypes });
  }

  handleIncludeTodayChanged(newIncludeToday) {
    this.recalculateReports({ includeToday: newIncludeToday });
  }

  handleIsIncludeGhostImpChanged(isIncludeGhostImp) {
    this.recalculateReports({ isIncludeGhostImp });
  }

  handleNoPbTypeChanged({ type }) {
    this.setState({ noPbType: type, selectedUprSegmentId: null });
    this.recalculateReports({ noPbType: type });
    this._changeQueryParams({ dataType: type });
  }

  async handleFilterClicked() {
    await this.getAnatomyData();
  }

  async getAnatomyData() {
    this.setState({
      isLoading: true,
      data: null,
      filteredData: null,
      selectedUprSegmentId: null,
      filterLatestDate: null,
    });

    try {
      const {
        segmentInfo,
        unitInfo,
        noPbType,
        demandTypes,
        aggregation,
        startDate,
        endDate,
        endDateNotIncludeToday,
        includeToday,
        isIncludeGhostImp,
      } = this.state;
      const { networkId, extUnitId, timezone } = unitInfo;

      // Get Segment Info if not existing
      let segmentList = [];
      if (_.isNil(segmentInfo)) {
        const { config } = await NetworkAPI.getNetworkUprSegmentation({
          networkId,
        });
        this.setState({ segmentInfo: config });
        segmentList = _.map(config, (seg) => seg.id);
      } else {
        segmentList = _.map(segmentInfo, (seg) => seg.id);
      }

      let et = moment.tz(endDate, timezone).unix();
      let st = moment.tz(startDate, timezone).unix();

      const params = {
        networkId,
        extGamUnitId: extUnitId,
        startDate: st,
        endDate: et,
      };
      const { dashboard } = await AnatomyAPI.getCstAnatomyReportLink(params);
      const data = transformReports(dashboard.data);
      _.forEach(data.upr_segments, (seg) => {
        seg.deprecated = segmentList.indexOf(seg.upr_segment_id) === -1;
      });

      // endDateNotIncludeToday
      const filterLatestDate = moment
        .tz(endDateNotIncludeToday, timezone)
        .unix();
      const filteredData = calculateDashboardReports({
        reports: data,
        noPbType,
        demandTypes,
        aggregation,
        costCpm: unitInfo.costCPMInCurrency,
        filterLatestDate: includeToday ? null : filterLatestDate,
        isIncludeGhostImp,
      });
      // console.log(filteredData);
      // console.log(unitInfo, filteredData);
      // console.log(filteredData.segment_performance_report);

      this.setState({
        data,
        filteredData,
        selectedGroupTypes: filteredData.groupTypes,
        filterLatestDate,
        hasDeprecatedSegments: _.some(
          data.upr_segments,
          (seg) => seg.deprecated
        ),
      });
    } catch (err) {
      console.log(err);
      this.setState({
        errorMsg: typeof err === "object" ? err.toString() : err,
      });
    }

    this.setState({ isLoading: false });
  }

  handleGetSegmentReport(segmentId, shouldScroll = true) {
    const result = this.updateSegmentData({
      filteredData: this.state.filteredData,
      segmentId,
    });

    this.setState({ selectedUprSegmentId: segmentId, ...result });

    if (shouldScroll) {
      setTimeout(() => {
        const element = document.getElementById("anatomy-chart-head");
        if (element) {
          window.scrollTo({
            behavior: "smooth",
            top: element.offsetTop,
          });
        }
      }, 500);
    }
  }

  updateSegmentData({ filteredData, segmentId }) {
    const segmentData = getSegmentReports(filteredData, segmentId);

    const detectorFloorPriceMap = getDetectorFloorPriceMap({
      reports: segmentData,
    });
    return { segmentData, detectorFloorPriceMap };
  }

  recalculateReports({
    aggregation,
    demandTypes,
    includeToday,
    isIncludeGhostImp,
    noPbType,
  }) {
    console.log("recalculate reports");
    // console.log(
    //   "recal",
    //   aggregation,
    //   demandTypes,
    //   includeToday,
    //   isIncludeGhostImp,
    //   this.state
    // );
    // time aggregation changed
    // demand types changed
    // console.log(aggregation, demandTypes);
    const { data, unitInfo, filterLatestDate } = this.state;

    const stateData = {};
    const params = {
      reports: data,
      demandTypes: this.state.demandTypes,
      aggregation: this.state.aggregation,
      noPbType: this.state.noPbType,
      costCpm: unitInfo.costCPMInCurrency,
      filterLatestDate: this.state.includeToday ? null : filterLatestDate,
      isIncludeGhostImp: this.state.isIncludeGhostImp,
    };

    if (aggregation) {
      params.aggregation = aggregation;
      stateData.aggregation = aggregation;
    } else if (noPbType) {
      params.noPbType = noPbType;
      stateData.noPbType = noPbType;
    } else if (demandTypes) {
      params.demandTypes = demandTypes;
      stateData.demandTypes = demandTypes;
    } else if (
      isIncludeGhostImp !== undefined &&
      isIncludeGhostImp !== this.state.isIncludeGhostImp
    ) {
      params.isIncludeGhostImp = isIncludeGhostImp;
      stateData.isIncludeGhostImp = isIncludeGhostImp;
    } else if (
      includeToday !== undefined &&
      includeToday !== this.state.includeToday
    ) {
      params.filterLatestDate = includeToday ? null : filterLatestDate;
      stateData.includeToday = includeToday;
    }

    if (_.keys(stateData).length > 0) {
      const filteredData = calculateDashboardReports(params);

      const result = this.updateSegmentData({
        filteredData,
        segmentId: this.state.selectedUprSegmentId,
      });
      this.setState({ filteredData, ...stateData, ...result });
    }
  }

  handleSelectedGroupTypes(groupTypes) {
    this.setState({ selectedGroupTypes: groupTypes });
  }

  handleTimezoneTypeChanged({ timezoneType }) {
    this.setState({ timezoneType });
  }

  handleSetExtremes({ min, max }) {
    console.log("set extremes", min, max);
    this.setState({ st: min, et: max });
  }

  render() {
    const { unitId } = this.props;
    const {
      isLoading,
      errorMsg,
      data,
      unitInfo,

      noPbType,

      dateRangeNum,
      startDate,
      endDate,

      demandTypePreset,
      selectedUprSegmentId,
      segmentData,
      detectorFloorPriceMap,

      demandTypes,
      aggregation,
      timezoneType,
      isIncludeGhostImp,

      filteredData,
      selectedGroupTypes,
      includeToday,

      st,
      et,

      isModalOpen,
      detailChartProps,

      // detector chart
      isDetectorModalOpen,
      detectorChartProps,

      // o vs d chart
      isOptVsDetectorModalOpen,

      isPublisherUprModalOpen,

      isOpenWarnModal,

      hasDeprecatedSegments,
    } = this.state;

    const infoList = unitInfo
      ? [
          {
            mainId: unitInfo.gamUnitId,
            mainTitle: unitInfo.name,
            subId: unitInfo.networkId,
            subTitle: unitInfo.networkName,
            timezone: unitInfo.timezone,
          },
        ]
      : [];

    const isPaused =
      unitInfo && (unitInfo.status === 1 || unitInfo.status === "PAUSED");

    return (
      <>
        {/* Diagnoser like header */}
        {/* <div className="border-b bg-gray-100 py-2 px-4">
          <div className="flex items-center justify-between">
            <div className="text-xl font-bold text-gray-800">CST Anatomy</div>

            <div className="flex justify-end gap-2">
              <GoToAnatomyButton unitId={unitId}></GoToAnatomyButton>
              <GoToInsightButton unitId={unitId}></GoToInsightButton>
              <GoToAPConfigButton unitId={unitId}></GoToAPConfigButton>
            </div>
          </div>
        </div> */}

        <div className="bg-blue-900 px-4 py-1">
          <div className="flex items-center justify-between">
            <div className="text-sm font-bold text-blue-100">CST Anatomy</div>
            <div className="flex justify-end gap-2">
              <GoToAnatomyButton unitId={unitId}></GoToAnatomyButton>
              <GoToInsightButton unitId={unitId}></GoToInsightButton>
              <GoToAPConfigButton unitId={unitId}></GoToAPConfigButton>
            </div>
          </div>
        </div>

        <UnitDetailHeader
          unitId={unitId}
          isPaused={isPaused}
          startDate={startDate}
          getUnitInfo={this.getUnitInfo}
        ></UnitDetailHeader>

        <FixedWidgetContainer>
          {infoList.length !== 0 && (
            <NetworkStickyTimeWidget
              itemRefs={[this.itemRef]}
              itemList={infoList}
            ></NetworkStickyTimeWidget>
          )}
        </FixedWidgetContainer>

        <MemoizedFilterProvider
          timezoneType={timezoneType}
          demandTypes={demandTypes}
          aggregation={aggregation}
          isIncludeGhostImp={isIncludeGhostImp}
          noPbType={noPbType}
          includeToday={includeToday}
          dateRangeNum={dateRangeNum}
          startDate={startDate}
          endDate={endDate}
          selectedGroupTypes={selectedGroupTypes}
          getFilterParams={this.getFilterParams}
        >
          <MemoizedDataProvider
            unitInfo={unitInfo}
            reports={data}
            segmentData={segmentData}
            selectedUprSegmentId={selectedUprSegmentId}
            filteredData={filteredData}
            hasDeprecatedSegments={hasDeprecatedSegments}
            handleGetSegmentReport={this.handleGetSegmentReport}
          >
            <MemoizedUIProvider
              st={st}
              et={et}
              isModalOpen={isModalOpen}
              isOptVsDetectorModalOpen={isOptVsDetectorModalOpen}
              isPublisherUprModalOpen={isPublisherUprModalOpen}
              isDetectorModalOpen={isDetectorModalOpen}
              detailChartProps={detailChartProps}
              detectorChartProps={detectorChartProps}
              handleCloseModal={this.handleCloseModal}
              handleSetExtremes={this.handleSetExtremes}
              setDemandTypeCompareProps={this.setDemandTypeCompareProps}
              setOVDProps={this.setOVDProps}
              setDetectorProps={this.setDetectorProps}
              setPublisherUprProps={this.setPublisherUprProps}
            >
              <div className="min-h-screen bg-gray-300 p-4" ref={this.itemRef}>
                {unitInfo && (
                  <>
                    <div className="-pb-4 mb-2 flex items-center justify-between bg-white px-4">
                      <div className="border-r pr-4 pt-3">
                        <AnatomyCstFilter
                          timezone={unitInfo.timezone}
                          timezoneType={timezoneType}
                          selectedDateRange={dateRangeNum}
                          startDate={startDate}
                          endDate={endDate}
                          refillStartEndDate={this.getStartDateEndDate}
                          handleFilterClicked={this.handleFilterClicked}
                        ></AnatomyCstFilter>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 pl-2 pt-5">
                        <AppliedUprButton />

                        <PublisherFirstLookButton />

                        <UnitDemandTypePortfolioButton
                          networkId={unitInfo.networkId}
                          gamUnitId={unitInfo.extUnitId}
                        />
                      </div>
                    </div>

                    {isLoading && <LoadingUI></LoadingUI>}

                    {errorMsg && <div className="text-red-600">{errorMsg}</div>}

                    {isPaused && !isLoading && (
                      <div className="shadow sticky top-0 z-50 my-2 flex items-center justify-center bg-orange-500 px-4 py-2 font-semibold text-white">
                        Unit is PAUSED
                      </div>
                    )}

                    {filteredData && (
                      <>
                        <div className="bg-white p-2">
                          <div className="flex gap-4">
                            <AnatomyTimeAggregationFilter
                              aggregation={aggregation}
                              handleAggregationChanged={this.recalculateReports}
                            ></AnatomyTimeAggregationFilter>

                            <AnatomyTimezoneTypeSelector
                              timezoneType={timezoneType}
                              handleTimezoneTypeChanged={
                                this.handleTimezoneTypeChanged
                              }
                            ></AnatomyTimezoneTypeSelector>

                            <AnatomyNoPbSelector
                              type={noPbType}
                              handleNoPbTypeChanged={this.handleNoPbTypeChanged}
                              data={filteredData}
                            ></AnatomyNoPbSelector>

                            <div className="p-4">
                              <div className="mb-1 font-semibold text-gray-700">
                                Show Latest Data:
                              </div>
                              <div className="flex items-center">
                                <label className="rounded flex cursor-pointer items-center">
                                  <Checkbox
                                    isChecked={includeToday}
                                    onChange={this.handleIncludeTodayChanged}
                                  ></Checkbox>
                                  <span className="ml-1 align-middle text-sm font-semibold text-gray-600 hover:text-gray-700">
                                    Include Today?
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>

                          <AnatomyDemandTypeFilter
                            demandTypePreset={demandTypePreset}
                            demandTypes={unitInfo.billableDemandTypes}
                            billableDemandTypes={unitInfo.billableDemandTypes}
                            handleDemandTypesChanged={
                              this.handleDemandTypesChanged
                            }
                            // recalculate={this.recalculateReports}
                          ></AnatomyDemandTypeFilter>

                          <IncludeGhostImprFilter
                            isIncludeGhostImp={isIncludeGhostImp}
                            handleChanged={this.handleIsIncludeGhostImpChanged}
                          ></IncludeGhostImprFilter>
                        </div>

                        <AnatomyCstSegmentsSection unitId={unitId} />

                        {selectedUprSegmentId && (
                          <>
                            <SegmentStickyHeader
                              isPaused={isPaused}
                              uprSegmentId={selectedUprSegmentId}
                              items={filteredData.upr_segments}
                              groupTypes={filteredData.groupTypes} // all groupTypes
                              selectedGroupTypes={selectedGroupTypes}
                              detectorFloorPriceMap={detectorFloorPriceMap}
                              handleSelectedGroupTypes={
                                this.handleSelectedGroupTypes
                              }
                            ></SegmentStickyHeader>

                            <CstAnatomyPriceSection />

                            <AnatomyCstPerformanceSection>
                              <AppliedUprButton />
                              <PublisherFirstLookButton />
                              <DemandTypePortfolioButton />
                            </AnatomyCstPerformanceSection>

                            {isModalOpen && <DemandTypeCompareModal />}

                            {isOptVsDetectorModalOpen && <OptVsDetectorModal />}
                            {/* This modal is open when clicked inside charts */}
                            {isDetectorModalOpen && <DetectorMetricModal />}
                          </>
                        )}
                      </>
                    )}

                    {/* Currently not in use */}
                    {isPublisherUprModalOpen && (
                      <PublisherUprModal
                        unitId={unitId}
                        networkId={unitInfo.networkId}
                        startDate={startDate}
                        includeToday={includeToday}
                        handleClose={this.handleCloseModal}
                      ></PublisherUprModal>
                    )}

                    <ModalWrapper
                      isOpen={isOpenWarnModal && isPaused}
                      showCloseFooter={true}
                      customOverlayStyle={{ zIndex: 100 }}
                      handleClose={() =>
                        this.setState({ isOpenWarnModal: false })
                      }
                      title={{
                        label: "Unit Status ",
                        highlight: `${unitId} - ${
                          unitInfo ? unitInfo.name : ""
                        }`,
                      }}
                      width="30%"
                    >
                      This unit is currently <b>PAUSED</b>
                    </ModalWrapper>
                  </>
                )}
              </div>
            </MemoizedUIProvider>
          </MemoizedDataProvider>
        </MemoizedFilterProvider>
      </>
    );
  }
}

function IncludeGhostImprFilter({ isIncludeGhostImp, handleChanged }) {
  return (
    <>
      <div className="-mt-4 flex items-center">
        <label className="flex cursor-pointer items-center px-4 py-2">
          <Checkbox
            isChecked={isIncludeGhostImp}
            onChange={() => handleChanged(!isIncludeGhostImp)}
          ></Checkbox>
          <span className="ml-1 align-middle text-sm font-semibold text-gray-600 hover:text-gray-700">
            Include $0 ghost impression in price priority
          </span>
        </label>
      </div>
    </>
  );
}

const MemoizedFilterProvider = React.memo(
  ({
    timezoneType,
    demandTypes,
    aggregation,
    isIncludeGhostImp,
    noPbType,
    includeToday,
    dateRangeNum,
    startDate,
    endDate,
    selectedGroupTypes,
    getFilterParams,
    children,
  }) => {
    const value = React.useMemo(
      () => ({
        timezoneType,
        demandTypes,
        aggregation,
        isIncludeGhostImp,
        noPbType,
        includeToday,
        dateRangeNum,
        startDate,
        endDate,
        selectedGroupTypes,
        getFilterParams,
      }),
      [
        timezoneType,
        demandTypes,
        aggregation,
        isIncludeGhostImp,
        noPbType,
        includeToday,
        dateRangeNum,
        startDate,
        endDate,
        selectedGroupTypes,
        getFilterParams,
      ]
    );

    return (
      <AnatomyFilterContext.Provider value={value}>
        {children}
      </AnatomyFilterContext.Provider>
    );
  }
);

const MemoizedDataProvider = React.memo(
  ({
    unitInfo,
    reports,
    segmentData,
    selectedUprSegmentId,
    filteredData,
    hasDeprecatedSegments,
    handleGetSegmentReport,
    children,
  }) => {
    const value = React.useMemo(
      () => ({
        unitInfo,
        reports,
        segmentData,
        selectedUprSegmentId,
        filteredData,
        hasDeprecatedSegments,
        handleGetSegmentReport,
      }),
      [
        unitInfo,
        reports,
        segmentData,
        selectedUprSegmentId,
        filteredData,
        hasDeprecatedSegments,
        handleGetSegmentReport,
      ]
    );

    return (
      <AnatomyDataContext.Provider value={value}>
        {children}
      </AnatomyDataContext.Provider>
    );
  }
);

const MemoizedUIProvider = React.memo(
  ({
    st,
    et,
    isModalOpen,
    isOptVsDetectorModalOpen,
    isPublisherUprModalOpen,
    isDetectorModalOpen,
    detailChartProps,
    detectorChartProps,
    handleCloseModal,
    handleSetExtremes,
    setDemandTypeCompareProps,
    setOVDProps,
    setDetectorProps,
    setPublisherUprProps,
    children,
  }) => {
    const value = React.useMemo(
      () => ({
        st,
        et,
        isModalOpen,
        isOptVsDetectorModalOpen,
        isPublisherUprModalOpen,
        isDetectorModalOpen,
        detailChartProps,
        detectorChartProps,
        handleCloseModal,
        handleSetExtremes,
        setDemandTypeCompareProps,
        setOVDProps,
        setDetectorProps,
        setPublisherUprProps,
      }),
      [
        st,
        et,
        isModalOpen,
        isOptVsDetectorModalOpen,
        isPublisherUprModalOpen,
        isDetectorModalOpen,
        detailChartProps,
        detectorChartProps,
        handleCloseModal,
        handleSetExtremes,
        setDemandTypeCompareProps,
        setOVDProps,
        setDetectorProps,
        setPublisherUprProps,
      ]
    );

    return (
      <AnatomyUIContext.Provider value={value}>
        {children}
      </AnatomyUIContext.Provider>
    );
  }
);

export default withRouter(AnatomyCSTViewer);
